import { Checklist } from "@/bryntum-lib/models/Checklist";
import { ChecklistItems } from "@/bryntum-lib/models/ChecklistItems";
import { Store } from "@bryntum/schedulerpro";
import { useCallback, useContext } from "react";
import { toast } from "sonner";
import { User } from "@/bryntum-lib/models/User";
import BryntumContext from "@/react/context/bryntumContext";
import { uploadImageToChecklistItem, checklistItemImageDelete } from "@/network/network";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";

export const ALLOWED_IMAGE_TYPES = ["png", "jpeg", "jpg", "svg", "heif"];

export const useSwivuChecklistItems = () => {
  const context = useContext(BryntumContext);
  const { project, user } = context;
  // @ts-expect-error - project is not typed
  const { checklistItemStore: checklistItemStoreInstance, boardUsersStore: boardUsersStoreInstance } = project;
  const checklistItemStore = checklistItemStoreInstance as Store;
  const boardUsersStore = boardUsersStoreInstance as Store;

  const getLastItemOrder = useCallback((store: Store) => {
    return (store.last as unknown as ChecklistItems)?.order || 0;
  }, []);

  const getChecklistItems = useCallback(
    (checklistRecordId: number) => {
      const items = checklistItemStore.query((item: ChecklistItems) => item.checklistId === checklistRecordId) as unknown as ChecklistItems[];
      // Sort by order property
      return items.sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    [checklistItemStore]
  );

  const canEditChecklistItem = useCallback(
    (checklistItem: ChecklistItems) => {
      const boardUser = boardUsersStore.find((boardUser: User) => boardUser.userId === user.id) as unknown as User | undefined;
      return user.can(checklistItem.checklistRecord.canEdit) || checklistItem.checklistRecord.assignedBoardUsers.includes(boardUser?.id);
    },
    [user, boardUsersStore]
  );

  const createChecklistItem = useCallback(
    (checklist: Checklist, description: string = "") => {
      const order = getLastItemOrder(checklistItemStore) + 1;
      const checklistItemRecord = checklistItemStore.createRecord({
        description,
        checklistId: checklist.id,
        archived: checklist.archived,
        status: 1,
        order
      });

      checklistItemStore.add(checklistItemRecord);
      return checklistItemRecord;
    },
    [checklistItemStore, getLastItemOrder]
  );

  // Update checklist item
  const updateChecklistItem = useCallback(
    (checklistItem: ChecklistItems, updates: Partial<ChecklistItems>) => {
      if (!canEditChecklistItem(checklistItem)) {
        toast("You don't have permission to edit this checklist item");
        return false;
      }

      Object.assign(checklistItem, updates);
      return true;
    },
    [canEditChecklistItem]
  );

  // Delete checklist item
  const deleteChecklistItem = useCallback(
    (checklistItem: ChecklistItems) => {
      if (!canEditChecklistItem(checklistItem)) {
        toast("You don't have permission to delete this checklist item");
        return false;
      }

      checklistItemStore.remove(checklistItem.id);
      return true;
    },
    [canEditChecklistItem, checklistItemStore]
  );

  // Toggle checklist item check status
  const toggleChecklistItem = useCallback(
    (checklistItem: ChecklistItems) => {
      const currentBoardUser = boardUsersStore.find((boardUser: User) => boardUser.userId === user.id) as unknown as User | undefined;
      const isAssigned = checklistItem.checklistRecord.assignedBoardUsers.includes(currentBoardUser?.id);

      if (!canEditChecklistItem(checklistItem) && !isAssigned) {
        toast("You don't have permission to modify this checklist item");
        return false;
      }

      checklistItem.isItemCheck = !checklistItem.isItemCheck;
      return true;
    },
    [canEditChecklistItem, boardUsersStore]
  );

  // Upload image to checklist item
  const uploadChecklistItemImage = useCallback(
    async (checklistItem: ChecklistItems, file: File) => {
      if (checklistItem.isPhantom) return null;

      const canEdit = canEditChecklistItem(checklistItem);

      if (!canEdit) {
        toast.error("You don't have permission to upload images to this checklist item");
        return false;
      }

      if (!file) {
        toast.error("No file selected");
        return false;
      }

      const fileType = file.type.split("/")[1];
      if (!file.type.includes("image") || !ALLOWED_IMAGE_TYPES.includes(fileType)) {
        toast.error(`Unsupported File Type. Please upload files with extensions: ${ALLOWED_IMAGE_TYPES.join(", ")}`);
        return false;
      }

      try {
        const selectedBoard = getSavedBoardSelection();
        const { data } = await uploadImageToChecklistItem(selectedBoard.id, checklistItem.id as number, file);

        // Update the store with the new image
        checklistItemStore.applyChangeset({
          updated: [
            {
              id: checklistItem.id,
              thumbnail_url: data.thumbnail_url + "?timestamp=" + Date.now(),
              image: data.image_url
            }
          ]
        });

        toast.success("Image uploaded successfully");
        return true;
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to upload image. Please try again.";
        toast.error(message);
        return false;
      }
    },
    [checklistItemStore, canEditChecklistItem]
  );

  // Remove image from checklist item
  const removeChecklistItemImage = useCallback(
    async (checklistItem: ChecklistItems) => {
      if (checklistItem.isPhantom) return null;

      const canEdit = canEditChecklistItem(checklistItem);
      const currentBoardUser = boardUsersStore.find((boardUser: User) => boardUser.userId === user.id) as unknown as User | undefined;
      const isAssigned = checklistItem.checklistRecord.assignedBoardUsers.includes(currentBoardUser?.id);

      if (!canEdit && !isAssigned) {
        toast.error("You don't have permission to remove this image");
        return false;
      }

      try {
        await checklistItemImageDelete(checklistItem.id as number);

        // Update the store to remove the image
        checklistItemStore.applyChangeset({
          updated: [{ id: checklistItem.id, thumbnailUrl: null, image: null }]
        });

        toast.success("Image removed successfully");
        return true;
      } catch (error) {
        toast.error("Failed to remove image. Please try again.");
        return false;
      }
    },
    [canEditChecklistItem, boardUsersStore, checklistItemStore]
  );

  // Reorder checklist items
  // This function handles the reordering of checklist items when they are dragged and dropped
  // It updates the order property of all affected items to maintain proper sorting
  const reorderChecklistItems = useCallback(
    (checklistId: number, dragIndex: number, hoverIndex: number) => {
      const items = getChecklistItems(checklistId);

      if (dragIndex === hoverIndex || dragIndex < 0 || hoverIndex < 0 || dragIndex >= items.length || hoverIndex >= items.length) {
        return false;
      }

      // Check if user can edit the checklist
      const firstItem = items[0];
      if (firstItem && !canEditChecklistItem(firstItem)) {
        toast.error("You don't have permission to reorder items in this checklist");
        return false;
      }

      // Create a copy of the items array
      const reorderedItems = [...items];

      // Remove the dragged item
      const draggedItem = reorderedItems.splice(dragIndex, 1)[0];

      // Insert it at the new position
      reorderedItems.splice(hoverIndex, 0, draggedItem);

      // Update the order property for all items
      reorderedItems.forEach((item, index) => {
        if (item.order !== index + 1) {
          item.order = index + 1;
        }
      });

      return true;
    },
    [getChecklistItems, canEditChecklistItem]
  );

  return {
    canEditChecklistItem,
    getChecklistItems,
    createChecklistItem,
    updateChecklistItem,
    deleteChecklistItem,
    toggleChecklistItem,
    uploadChecklistItemImage,
    removeChecklistItemImage,
    reorderChecklistItems
  };
};
